<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import { home } from '@/routes';

const { t } = useI18n();

const loginImageCount = 6;
const loginImage = `/images/auth/login-${Math.floor(Math.random() * loginImageCount) + 1}.webp`;

defineProps<{
    title?: string;
    description?: string;
    panelEyebrow?: string;
    panelHeadline?: string;
    panelSub?: string;
    panelStats?: { value: string; label: string }[];
    panelSteps?: { label: string; active: boolean }[];
    panelCard?: { title: string; body: string };
}>();
</script>

<template>
    <div class="flex min-h-dvh w-full bg-background font-geist text-foreground">
        <!-- Form column -->
        <div class="flex min-w-0 flex-1 flex-col px-8 py-8 sm:px-14">
            <Link :href="home()" class="inline-flex items-center gap-2 no-underline">
                <AppLogoIcon class="h-[22px] w-[18px] shrink-0 text-foreground" />
                <span class="text-[15px] font-semibold tracking-tight text-foreground">Novantt</span>
            </Link>

            <div class="mx-auto flex w-full max-w-[420px] flex-1 flex-col justify-center">
                <slot />
            </div>

            <div class="flex gap-4 text-[11px] text-muted-foreground">
                <span>&copy; Novantt {{ new Date().getFullYear() }}</span>
                <a href="/terms" class="text-muted-foreground no-underline hover:text-foreground">{{ t('auth.footer.terms') }}</a>
                <a href="/privacy" class="text-muted-foreground no-underline hover:text-foreground">{{ t('auth.footer.privacy') }}</a>
            </div>
        </div>

        <!-- Brand panel -->
        <div class="relative hidden w-[520px] shrink-0 flex-col overflow-hidden p-10 text-white lg:flex">
            <img :src="loginImage" alt="" class="pointer-events-none absolute inset-0 h-full w-full object-cover" />
            <div class="pointer-events-none absolute inset-0 bg-black/75"></div>

            <div class="relative flex flex-1 flex-col">
                <div v-if="panelEyebrow" class="text-[11px] font-medium uppercase tracking-wider text-white/55">{{ panelEyebrow }}</div>

                <div class="mt-auto">
                    <div v-if="panelHeadline" class="text-[28px] font-semibold leading-snug tracking-tight lg:text-[32px]">{{ panelHeadline }}</div>
                    <div v-if="panelSub" class="mt-3.5 text-[13px] leading-relaxed text-white/65">{{ panelSub }}</div>

                    <!-- Steps (register) -->
                    <div v-if="panelSteps?.length" class="mt-5 flex flex-col gap-3">
                        <div v-for="(step, i) in panelSteps" :key="i" class="flex items-center gap-3">
                            <span
                                class="flex size-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                                :class="step.active ? 'bg-[#3B5BDB] text-white' : 'border border-white/20 text-white/50'"
                            >{{ i + 1 }}</span>
                            <span class="text-[13px]" :class="step.active ? 'font-medium text-white' : 'text-white/50'">{{ step.label }}</span>
                        </div>
                    </div>

                    <!-- Card (forgot password) -->
                    <div v-if="panelCard" class="mt-7 rounded-lg border border-white/12 bg-white/5 p-4">
                        <div class="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-white/50">{{ panelCard.title }}</div>
                        <div class="text-[13px] leading-relaxed text-white">{{ panelCard.body }}</div>
                    </div>
                </div>

                <!-- Stats (login) -->
                <div v-if="panelStats?.length" class="mt-7 flex gap-6 text-[11px] text-white/55">
                    <div v-for="stat in panelStats" :key="stat.label">
                        <div class="text-lg font-semibold text-white">{{ stat.value }}</div>
                        {{ stat.label }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
